<script lang="ts">
    import { onDestroy, onMount } from 'svelte'
    import type { Readable, Writable } from 'svelte/store'
    import { fade } from 'svelte/transition'

    import { isEqual } from 'lodash-es'
    import { IPv4 } from 'ip-num'
    import { collapseIPv6Number } from 'ip-num'

    import { resolve, gettext as _ } from 'hydria/services/gettext'

    import { shapeRecords } from 'wdom/zone/record_schemas'
    import { TTL_TICKS_S_WITH_DEFAULT, tickValueToText, removeDomainFromTarget } from 'wdom/zone/zone'
    import type { NewEditableRecord, Zone, ZoneTemplate } from 'wdom/zone/zone'

    import { keyTexts, typeKeys, typeDescriptions, typeExtraDescriptions, types } from 'wdom/zone/zone'
    import type { EditableRecord } from 'wdom/zone/zone'

    import MiniKeyboard from 'wdom/components/MiniKeyboard.svelte'
    import Dropdown from 'hydria/components/Dropdown.svelte'
    import { toASCII, toUnicode } from 'punycode-esm'
    import InputRange from 'hydria/components/InputRange.svelte'

    // These types have a `target` field that is a dns entry (vs A or AAAA, having an IP)
    const IDNA_TARGET_TYPES = ['ANAME', 'CNAME', 'DNAME', 'MX', 'NS', 'SRV']

    interface Props {
        rg: EditableRecord | NewEditableRecord
        status: Writable<'loading' | 'ready' | 'not-found' | 'error'>
        zone: Writable<Zone | ZoneTemplate | undefined>
        zoneUnicode: Readable<string | undefined>
        saveRecord?: (rg: EditableRecord) => void
        close: () => void
    }

    let { rg = $bindable(), status, zone, zoneUnicode, saveRecord, close }: Props = $props()

    // The field that we are editing, compared to rg.edited, which is our "original values"
    const edit: any = $state({ ...rg.edited })

    // Some fields that we want to edit as unicode, but are stored as IDNA.
    // These fields are reactively converted from editUnicode to edit as we type,
    // so yup's validation work as intended.
    // `target` should only be converted when not A or AAAA, which are not idna but IPs.
    const editUnicode = $state({
        idna: toUnicode(edit.idna),
        target: edit.target !== undefined && IDNA_TARGET_TYPES.includes(edit.type) ? toUnicode(edit.target) : null,
    })
    $effect(() => {
        edit.idna = toASCII(editUnicode.idna.toLowerCase())
    })
    $effect(() => {
        if (edit.target !== undefined && editUnicode.target != null) edit.target = toASCII(editUnicode.target)
    })

    let ttlValue = $state(TTL_TICKS_S_WITH_DEFAULT.indexOf(edit.ttl))
    $effect(() => {
        edit.ttl = TTL_TICKS_S_WITH_DEFAULT[ttlValue]
    })

    // The keys on the `edit` object
    let localTypeKeys = !edit.type ? [] : typeKeys[typeKeys[edit.type] != null ? edit.type : 'default']

    // Helper function to simplify that try/catch bullshit when we only want a boolean
    function validateSyncAt(key: string, ...deps: Array<any>) {
        try {
            shapeRecords[edit.type].validateSyncAt(key, edit)
            return true
        } catch {
            return false
        }
    }

    // Change the type of the edit object.
    // Special care has to be taken for editUnicode.target.
    function setType(newType: string) {
        localTypeKeys = typeKeys[typeKeys[newType] != null ? newType : 'default']
        for (const key of localTypeKeys) {
            if (key == 'target' && IDNA_TARGET_TYPES.includes(newType)) {
                if (edit[key]) {
                    editUnicode[key] = toUnicode(edit[key])
                } else {
                    edit[key] = ''
                    editUnicode[key] = ''
                }
            } else if (key == 'target') {
                // A, AAAA, we don't want to convert editUnicode -> edit
                editUnicode[key] = null
                if (!edit[key]) edit[key] = null
            } else if (edit[key] === undefined) {
                edit[key] = null
            }
        }
        // Set only at this point to prevent reactivity problems
        edit.type = newType
        for (const key of Object.keys(edit)) {
            if (['idna', 'type', 'ttl'].includes(key)) continue
            if (!localTypeKeys.includes(key)) {
                delete edit[key]
            }
        }
    }

    let formIsValid = $derived(edit.type ? shapeRecords[edit.type].isValidSync(edit) : false)
    let isChanged = $derived(!isEqual(edit, rg.edited))
    // $: console.log('edit:', edit, editUnicode)
    // $: {
    //     try {
    //         shapeRecords[edit.type].validate(edit)
    //     } catch (e) {
    //         console.log('error:', e)
    //     }
    // }

    function save() {
        rg.edited = { ...edit }
        if (IDNA_TARGET_TYPES.includes(edit.type)) {
            rg.edited['target'] = removeDomainFromTarget((<Zone>$zone).idna != null ? (<Zone>$zone).idna : 'zone.tpl', rg.edited['target'])
        } else if (edit.type == 'AAAA') {
            const m = rg.edited['target'].match(/^(?<pre>(0{0,4}:)+:ffff:)(?<v4>\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})$/)
            let value: string
            if (m) {
                const ipHex = IPv4.fromDecimalDottedString(m.groups['v4']).value.toString(16).padStart(8, '0')

                value = m.groups['pre'] + ipHex.slice(0, 4) + ':' + ipHex.slice(4)
            } else {
                value = rg.edited['target']
            }
            value = collapseIPv6Number(value)
            if (value.startsWith('::ffff:') && value.length == 16) {
                // Convert ipv4 to a "visually understandable" ipv4-as-ipv6
                let v4Parts = [value.slice(7, 9), value.slice(9, 11), value.slice(12, 14), value.slice(14, 16)]
                value = '::ffff:' + v4Parts.map((v) => parseInt(v, 16).toString()).join('.')
            }
            rg.edited['target'] = value
        } else if (edit.type == 'A') {
            rg.edited['target'] = IPv4.fromDecimalDottedString(rg.edited['target']).toString()
        }
        if (rg.status != null && rg.status != 'new') rg.status = !isEqual(rg.edited, rg.saved) ? 'changed' : 'saved'

        close()
    }

    let idnaElement: HTMLElement | undefined = $state()
    onMount(() => {
        if (idnaElement) idnaElement.focus()
    })

    $effect(() => {
        if ($status == 'ready' && !document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.add('modal-open')
        } else if ($status != 'ready' && document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.remove('modal-open')
        }
    })

    onDestroy(() => {
        if ($status == 'ready' && document.documentElement.classList.contains('modal-open')) document.documentElement.classList.remove('modal-open')
    })
</script>

<style>
    .col-form-label {
        text-align: right;
        font-weight: bold;
    }
</style>

<svelte:window
    onkeydown={(e) => {
        if (e.key == 'Escape') close()
        else if (e.shiftKey && e.key == 'Enter') save()
    }}
/>

{#if $status == 'ready'}
    <div class="modal" role="dialog" transition:fade={{ duration: 250 }}>
        <div class="modal-dialog modal-lg">
            <div class="modal-content">
                <div class="modal-header text-bg-primary"><div>{rg.status == 'new' ? $_('New zone record') : $_('Zone record edition')}</div></div>

                <form
                    onsubmit={(e) => {
                        e.preventDefault()
                        save()
                    }}
                    autocomplete="off"
                >
                    <div class="modal-body d-grid gap-3">
                        <div class="form-group row">
                            <label for="zrec-name" class="col-sm-3 col-form-label">{$_('Name')}</label>
                            <div class="col-sm-9 d-flex gap-2">
                                <div class="input-group">
                                    <input type="text" class="form-control" id="zrec-name" class:is-invalid={false} autocomplete="off" bind:this={idnaElement} bind:value={editUnicode.idna} />
                                    <div class="input-group-text">.{$zoneUnicode}</div>
                                </div>
                                <div><MiniKeyboard modal={true} eid={'zrec-name'} /></div>
                            </div>
                        </div>
                        <div class="form-group row">
                            <label for="zrec-ttl" class="col-sm-3 col-form-label">{$_('TTL')}</label>
                            <div class="col-sm-6">
                                <InputRange min={0} max={TTL_TICKS_S_WITH_DEFAULT.length - 1} bind:value={ttlValue} id="zrec-ttl" />
                            </div>
                            <div class="col-sm-3 text-end">
                                <p class="form-control-plaintext">{$resolve(tickValueToText(TTL_TICKS_S_WITH_DEFAULT[ttlValue]))}</p>
                            </div>
                        </div>
                        <div class="form-group row">
                            <label for="zrec-type" class="col-sm-3 col-form-label">{$_('Type')}</label>
                            <div class="col-sm-9">
                                <Dropdown
                                    items={types}
                                    placeholder={$_('Select a record type')}
                                    onSelect={(e) => {
                                        setType(e)
                                    }}
                                    text={(t) => `${t} – ${$resolve(typeDescriptions[t])}`}
                                    selectedText={(t) => t}
                                    id={null}
                                    selected={edit.type}
                                />
                                {#if typeExtraDescriptions[edit.type] != null}
                                    <div class="form-text">
                                        <span>{$resolve(typeExtraDescriptions[edit.type])}</span>
                                    </div>
                                {/if}
                            </div>
                        </div>
                        {#each ['preference', 'flags', 'tag', 'priority', 'weight', 'port'] as key}
                            {#if edit[key] !== undefined}
                                <div class="form-group row">
                                    <label for="zrec-{key}" class="col-sm-3 col-form-label">{$resolve(keyTexts[key])}</label>
                                    <div class="col-sm-9">
                                        {#if key == 'tag'}
                                            <input type="text" class="form-control" id="zrec-{key}" bind:value={edit[key]} autocomplete="off" class:is-invalid={!validateSyncAt(key, edit)} />
                                        {:else}
                                            <input type="number" class="form-control" id="zrec-{key}" bind:value={edit[key]} autocomplete="off" class:is-invalid={!validateSyncAt(key, edit)} />
                                        {/if}
                                    </div>
                                </div>
                            {/if}
                        {/each}
                        {#if edit.target !== undefined}
                            <div class="form-group row">
                                <label for="zrec-target" class="col-sm-3 col-form-label">{$resolve(keyTexts.target)}</label>
                                <div class="col-sm-9">
                                    {#if IDNA_TARGET_TYPES.includes(edit.type)}
                                        <div class="input-group">
                                            <input type="text" id="zrec-target" class="form-control monospace" autocomplete="off" bind:value={editUnicode.target} class:is-invalid={!validateSyncAt('target', edit)} />
                                            <MiniKeyboard eid={'zrec-target'} modal={true} />
                                        </div>
                                        {#if edit.target === '.' && edit.type == 'SRV'}
                                            <p class="form-text">{$_('A target of “.” indicates that the service is not available at this domain.')}</p>
                                        {:else if edit.target != null && edit.target != '' && !edit.target.endsWith('.') && edit.target.includes('.') && validateSyncAt('target', edit)}
                                            <p class="form-text"><span class="text-warning">{@html $_('Without a dot (“.”) at the end, the effective target will be <strong>{target}.{zone}.</strong>, which may not be what is expected.', { target: toUnicode(edit.target), zone: $zoneUnicode })}</span></p>
                                        {:else if edit.target != null && edit.target != '' && validateSyncAt('target', edit)}
                                            <p class="form-text">{@html $_('Effective target: <strong>{eff_target}</strong>', { eff_target: edit.target == '@' ? `${$zoneUnicode}.` : edit.target.endsWith('.') ? toUnicode(edit.target) : `${toUnicode(edit.target)}.${$zoneUnicode}.` })}</p>
                                        {/if}
                                    {:else}
                                        <input type="text" autocomplete="off" class="form-control monospace" bind:value={edit.target} class:is-invalid={!validateSyncAt('target', edit)} placeholder={edit.type == 'A' ? 'x.x.x.x' : edit.type == 'AAAA' ? 'xxxx:xxxx:xxxx:xxxx:xxxx:xxxx:xxxx:xxxx' : null} />
                                    {/if}
                                </div>
                            </div>
                        {/if}
                        {#if edit.value !== undefined}
                            <div class="form-group row">
                                <label for="zrec-value" class="col-sm-3 col-form-label">{$resolve(keyTexts.value)}</label>
                                <div class="col-sm-9">
                                    <input type="text" autocomplete="off" class="form-control monospace" bind:value={edit.value} class:is-invalid={!validateSyncAt('value', edit)} />
                                </div>
                            </div>
                        {/if}
                    </div>

                    <div class="modal-footer">
                        <button class="btn btn-outline-secondary" type="button" onclick={() => close()}>{$_('Cancel')}</button>
                        <button class="btn btn-primary" type="submit" disabled={!formIsValid || !isChanged}>{$_('Save')}</button>
                    </div>
                </form>
            </div>
        </div>
        <div class="modal-backdrop" aria-hidden="true" onclick={() => close()}></div>
    </div>
{/if}
